import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { comprimirFoto, enviarInspecao, obterMensagemErroEnvio } from '../services/inspecaoService';
import { criarOuObterInspecao, excluirFoto, listarFotos, salvarFoto } from '../services/inspecaoDb';
import { Carregando } from '../components';

export default function FotosInspecao({ placa, onNovaInspecao, onSessaoExpirada, setAviso }) {
  const idUsuario = useSelector((state) => state.auth.user && state.auth.user.id);
  const inputCamera = useRef(null);
  const inputGaleria = useRef(null);

  const [inspecaoId, setInspecaoId] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [urls, setUrls] = useState({});
  const [processando, setProcessando] = useState('');
  const [progresso, setProgresso] = useState(0);
  const [fotoAmpliada, setFotoAmpliada] = useState(null);

  const atualizarFotos = useCallback(async (id) => {
    setFotos(await listarFotos(id));
  }, []);

  useEffect(() => {
    let ativo = true;
    criarOuObterInspecao(placa)
      .then(async (id) => {
        if (!ativo) return;
        setInspecaoId(id);
        await atualizarFotos(id);
      })
      .catch(() => setAviso({
        titulo: 'Erro',
        mensagem: 'Não foi possível preparar o armazenamento local da inspeção. Verifique se o navegador não está em modo anônimo e tente novamente.',
      }));
    return () => { ativo = false; };
  }, [placa, atualizarFotos, setAviso]);

  // Gera as URLs de visualização das fotos e libera as antigas.
  useEffect(() => {
    const novas = {};
    fotos.forEach((foto) => { novas[foto.id] = URL.createObjectURL(foto.blob); });
    setUrls(novas);
    return () => Object.values(novas).forEach((url) => URL.revokeObjectURL(url));
  }, [fotos]);

  const adicionarFotos = async (e) => {
    const arquivos = Array.from(e.target.files || []);
    e.target.value = '';
    if (!arquivos.length || !inspecaoId) return;

    setProcessando(arquivos.length > 1 ? `Salvando ${arquivos.length} fotos...` : 'Salvando foto...');
    let falhas = 0;
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const arquivo of arquivos) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await salvarFoto(inspecaoId, await comprimirFoto(arquivo));
        } catch (erro) {
          falhas += 1;
        }
      }
      await atualizarFotos(inspecaoId);
      if (falhas) {
        setAviso({ titulo: 'Erro', mensagem: `Não foi possível salvar ${falhas} foto(s).` });
      }
    } finally {
      setProcessando('');
    }
  };

  const confirmarExclusao = (foto) => setAviso({
    titulo: 'Excluir foto',
    mensagem: 'Deseja remover esta foto?',
    textoConfirmar: 'Excluir',
    destrutivo: true,
    onConfirmar: async () => {
      await excluirFoto(foto.id);
      setFotoAmpliada(null);
      await atualizarFotos(inspecaoId);
    },
  });

  const enviar = async () => {
    if (!fotos.length) {
      setAviso({ titulo: 'Fotos necessárias', mensagem: 'Inclua pelo menos uma foto antes do envio.' });
      return;
    }
    if (!idUsuario) {
      setAviso({ titulo: 'Usuário inválido', mensagem: 'Não foi possível identificar o usuário conectado. Entre novamente.' });
      return;
    }
    if (navigator.onLine === false) {
      setAviso({
        titulo: 'Internet desconectada',
        mensagem: 'O aparelho está sem acesso à internet. Ative o Wi-Fi ou os dados móveis antes de enviar. As fotos continuam salvas neste aparelho.',
      });
      return;
    }

    setProgresso(0);
    setProcessando('Enviando fotos...');
    try {
      // envia na ordem em que as fotos foram tiradas
      const fotosEnvio = [...fotos].sort((a, b) => a.id - b.id);
      await enviarInspecao(idUsuario, inspecaoId, placa, fotosEnvio, setProgresso);
      setFotos([]);
      setAviso({
        titulo: 'Enviado',
        mensagem: 'A inspeção foi enviada e as fotos foram removidas deste aparelho.',
        onFechar: onNovaInspecao,
      });
    } catch (erro) {
      const erroEnvio = obterMensagemErroEnvio(erro);
      setAviso({
        titulo: erroEnvio.titulo,
        mensagem: erroEnvio.mensagem,
        onFechar: erroEnvio.sessaoExpirada ? onSessaoExpirada : undefined,
      });
    } finally {
      setProcessando('');
    }
  };

  return (
    <div className="oficina-pagina oficina-fotos">
      <div className="oficina-placa-cabecalho">
        <button type="button" className="oficina-icone-botao" aria-label="Voltar" onClick={onNovaInspecao} disabled={Boolean(processando)}>
          <i className="pi pi-arrow-left" />
        </button>
        <div className="oficina-placa" aria-label={`Placa ${placa}`}>{placa}</div>
        <span className="oficina-contador">{fotos.length} foto(s)</span>
      </div>
      <p className="oficina-instrucao">Fotografe os lados, interior e eventuais avarias do veículo.</p>

      <div className="oficina-acoes-foto">
        <button type="button" className="oficina-botao oficina-botao-primario" disabled={!inspecaoId || Boolean(processando)} onClick={() => inputCamera.current.click()}>
          <i className="pi pi-camera" /> Tirar foto
        </button>
        <button type="button" className="oficina-botao oficina-botao-secundario" disabled={!inspecaoId || Boolean(processando)} onClick={() => inputGaleria.current.click()}>
          <i className="pi pi-images" /> Galeria
        </button>
        <input ref={inputCamera} type="file" accept="image/*" capture="environment" hidden onChange={adicionarFotos} />
        <input ref={inputGaleria} type="file" accept="image/*" multiple hidden onChange={adicionarFotos} />
      </div>

      {fotos.length ? (
        <ul className="oficina-grade">
          {fotos.map((foto) => (
            <li key={foto.id}>
              <button type="button" className="oficina-miniatura" onClick={() => setFotoAmpliada(foto)}>
                {urls[foto.id] ? <img src={urls[foto.id]} alt="Foto da inspeção" /> : null}
              </button>
              <button type="button" className="oficina-excluir-foto" aria-label="Excluir foto" onClick={() => confirmarExclusao(foto)} disabled={Boolean(processando)}>
                <i className="pi pi-trash" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="oficina-vazio">Nenhuma foto adicionada.</p>
      )}

      <div className="oficina-rodape-acoes">
        <button type="button" className="oficina-botao oficina-botao-envio oficina-botao-bloco" disabled={Boolean(processando) || !fotos.length} onClick={enviar}>
          <i className="pi pi-cloud-upload" /> Enviar fotos
        </button>
        <button type="button" className="oficina-botao oficina-botao-texto oficina-botao-bloco" disabled={Boolean(processando)} onClick={onNovaInspecao}>
          Nova inspeção
        </button>
      </div>

      {processando ? (
        <div className="oficina-modal-fundo">
          <div className="oficina-modal oficina-modal-progresso">
            <Carregando texto={processando} />
            {processando.startsWith('Enviando') ? (
              <div className="oficina-barra" aria-label="Progresso do envio"><span style={{ width: `${progresso}%` }} /></div>
            ) : null}
          </div>
        </div>
      ) : null}

      {fotoAmpliada && urls[fotoAmpliada.id] ? (
        <div className="oficina-visualizador" role="dialog" aria-modal="true" onClick={() => setFotoAmpliada(null)}>
          <img src={urls[fotoAmpliada.id]} alt="Foto da inspeção ampliada" />
          <div className="oficina-visualizador-acoes">
            <button type="button" className="oficina-botao oficina-botao-secundario" onClick={() => setFotoAmpliada(null)}>Fechar</button>
            <button type="button" className="oficina-botao oficina-botao-perigo" onClick={(e) => { e.stopPropagation(); confirmarExclusao(fotoAmpliada); }}>
              <i className="pi pi-trash" /> Excluir
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
